import { registerPageInit } from '../barba/page-init-registry';
import '../../css/_root.scss';
import '../../css/pages/personal-area/personal-area.scss';
import '../../css/pages/personal-area/personal-area-table.scss';
import '../../css/pages/personal-area/settings.scss';
import '../../css/pages/personal-area/manager-notifications.scss';
import '../../css/pages/personal-area/general-information.scss';
import '../../css/pages/personal-area/manager-residents-list.scss';
import '../../css/pages/personal-area/manager-reports-list.scss';
import '../../css/pages/personal-area/manager-filter-drawer.scss';
import '../../css/pages/personal-area/report/resident-report-detail.scss';
import { TabWidget } from '../widgets/tab-widget';
import { initPasswordChangeModal } from '../widgets/password-change-modal';
import { initLogoutConfirmModal } from '../widgets/logout-confirm-modal';
import { initLkMobileNotice } from '../widgets/lk-mobile-notice';
import { getBitrixSessid } from '../utils/getBitrixSessid';
import { getApiUrl } from '../utils/getApiUrl';
import {
  showStcErrorNotification,
  showStcSuccessNotification,
} from '@/js/components/stc-notification';

import { initManagerNotifications } from '@/js/widgets/lk-manager-notifications';
import { initManagerResidents } from '@/js/widgets/lk-manager-residents';
import { initManagerReports } from '@/js/widgets/lk-manager-reports';
import { initNotificationsSidebarBadge } from '../utils/notifications-sidebar-badge';

class PersonalManagerResidentsArea {
  private static readonly SETTINGS_FORM_SELECTOR = '#residentSettingsForm';
  private static readonly SETTINGS_FORM_BOUND_ATTR = 'residentSettingsFormBound';
  private managerTabWidget: TabWidget;
  private readonly settingsSubmitUrl: string;
  private isSettingsSubmitting: boolean = false;

  constructor() {
    this.managerTabWidget = new TabWidget({
      rootSelector: '.lk-page',
    });

    this.settingsSubmitUrl = getApiUrl('/api/form/settings/');
  }

  public init(root?: ParentNode): void {
    this.bindEvents();
    initLogoutConfirmModal(root);
    initPasswordChangeModal(root);
    initLkMobileNotice(root);

    const queryTab = this.getTabIdFromUrl();
    this.managerTabWidget.init(queryTab ?? undefined, (tabId: number | undefined) => {
      this.setTabIdToUrl(tabId);
    });

    initManagerResidents(root);
    initManagerReports(root);
    initManagerNotifications(root);
    initNotificationsSidebarBadge(root);
  }

  private bindEvents(): void {
    const settingsForm = document.querySelector<HTMLFormElement>(
      PersonalManagerResidentsArea.SETTINGS_FORM_SELECTOR,
    );
    if (!settingsForm) return;

    if (settingsForm.dataset[PersonalManagerResidentsArea.SETTINGS_FORM_BOUND_ATTR] === '1') {
      return;
    }

    settingsForm.addEventListener('change', async (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement) || target.type !== 'checkbox') {
        return;
      }

      await this.submitSettingsAsJson(settingsForm);
    });
    settingsForm.dataset[PersonalManagerResidentsArea.SETTINGS_FORM_BOUND_ATTR] = '1';
  }

  private getSettingsCheckboxValue(form: HTMLFormElement, names: string[]): boolean {
    for (const name of names) {
      const input = form.querySelector<HTMLInputElement>(`input[name="${name}"]`);
      if (input) {
        return input.checked;
      }
    }

    return false;
  }

  private async submitSettingsAsJson(form: HTMLFormElement): Promise<void> {
    if (this.isSettingsSubmitting) return;

    const sessid = getBitrixSessid();
    if (!sessid) {
      console.error('Не удалось получить сессию пользователя');
      return;
    }

    const notifyManagerMessage = this.getSettingsCheckboxValue(form, [
      'notify_manager_message',
      'notify_new_message',
    ]);
    const notifyStatusReport = this.getSettingsCheckboxValue(form, ['notify_status_report']);
    const notifyChangeResident = this.getSettingsCheckboxValue(form, ['notify_change_resident']);

    this.isSettingsSubmitting = true;
    try {
      const response = await fetch(this.settingsSubmitUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Bitrix-Csrf-Token': sessid,
        },
        body: JSON.stringify({
          notify_manager_message: notifyManagerMessage ? 1 : 0,
          notify_status_report: notifyStatusReport ? 1 : 0,
          notify_change_resident: notifyChangeResident ? 1 : 0,
          sessid,
        }),
      });

      const result = (await response.json()) as {
        msg?: string;
        status?: 'success' | 'error';
      };

      if (!response.ok || result.status === 'error') {
        showStcErrorNotification(result.msg || 'Ошибка обработки');
        return;
      }

      showStcSuccessNotification(result.msg || 'Данные успешно сохранены');
    } catch {
      showStcErrorNotification('Ошибка соединения');
    } finally {
      this.isSettingsSubmitting = false;
    }
  }

  private getTabIdFromUrl(): number | null {
    const params = new URLSearchParams(window.location.search);
    const tabId = params.get('tab');
    return tabId ? Number(tabId) : null;
  }

  private setTabIdToUrl(tabId?: number): void {
    if (tabId === null || tabId === undefined) return;

    const params = new URLSearchParams(window.location.search);
    params.set('tab', String(tabId));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }
}

registerPageInit('personal-manager-residents-area', (root?: ParentNode) => {
  const page = new PersonalManagerResidentsArea();
  page.init(root);
});
